import React from 'react';
import { L } from '../../styles/theme';

interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Color tint for the icon and (optionally) the value. */
  color?: string;
  /** Icon shown next to the label (small, muted). */
  icon?: React.ReactNode;
  /** Static suffix shown after the value (e.g. "%"). */
  suffix?: React.ReactNode;
  /** When true, applies `color` to the value. */
  colorValue?: boolean;
  /** Optional click handler. */
  onClick?: () => void;
  /** Optional third line under the value (e.g. "+3 за неделю"). */
  hint?: React.ReactNode;
}

/**
 * Ячейка стат-строки «Линии». Используется ТОЛЬКО внутри <StatRow> —
 * ряд ячеек в одной рамке, разделённых тонкими линиями (не карточки).
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  color,
  icon,
  suffix,
  colorValue,
  onClick,
  hint,
}) => {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '11px 16px 12px',
        cursor: onClick ? 'pointer' : 'default',
        minWidth: 0,
        flex: 1,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: L.muted,
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {icon ? <span style={{ color: color || L.faint, fontSize: 12, display: 'inline-flex' }}>{icon}</span> : null}
        {label}
      </div>
      <div
        className="num"
        style={{
          fontSize: 18,
          fontWeight: 500,
          letterSpacing: '-0.02em',
          color: colorValue && color ? color : L.ink,
        }}
      >
        {value}
        {suffix ? <span style={{ fontSize: 12, color: L.faint, marginLeft: 3 }}>{suffix}</span> : null}
      </div>
      {hint ? <div style={{ fontSize: 11, color: L.faint, marginTop: 3 }}>{hint}</div> : null}
    </div>
  );
};

interface StatRowProps {
  children: React.ReactNode;
  /** Kept for API compatibility with old grid version (ignored). */
  gutter?: number;
  span?: number;
  /** Override default bottom margin. */
  marginBottom?: number;
}

/**
 * Стат-строка «Линии»: одна рамка, ячейки через вертикальные разделители.
 *   <StatRow>
 *     <StatCard label="Всего" value={42} />
 *     <StatCard label="Активных" value={18} color={L.ok} colorValue />
 *   </StatRow>
 */
export const StatRow: React.FC<StatRowProps> = ({ children, marginBottom = 16 }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div
      style={{
        display: 'flex',
        border: `1px solid ${L.line}`,
        borderRadius: 10,
        background: L.surface,
        boxShadow: L.shadow,
        marginBottom,
        overflow: 'hidden',
      }}
    >
      {items.map((child, i) => (
        <React.Fragment key={i}>
          {i > 0 && <div style={{ width: 1, background: L.lineSoft, flex: 'none' }} />}
          {child}
        </React.Fragment>
      ))}
    </div>
  );
};

export default StatCard;
